/* RESPONSIBLE TEAM: team-frontend-tech */
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import ENV from 'embercom/config/environment';
import { get, put } from 'embercom/lib/ajax';
import generateUUID from 'embercom/lib/uuid-generator';
import AdminSummary from 'embercom/objects/inbox/admin-summary';
import AdminWithPermissions, {
  type AdminWithPermissionsWireFormat,
  AdminPermissions,
} from 'embercom/objects/inbox/admin-with-permissions';
import type Conversation from 'embercom/objects/inbox/conversation';
import type CopilotApi from 'embercom/services/copilot-api';
import { type default as IntlService, DEFAULT_LOCALE } from 'embercom/services/intl';
import type RegionService from 'embercom/services/region-service';
import type SuiteMigrationService from 'embercom/services/suite-migration-service';
import { type ValidLocales } from '../components/inbox2/user-menu/locale-switcher';
import Workspace, { type WorkspaceForList } from '../objects/workspace';
import type FinStandaloneService from './fin-standalone-service';
import { Channel } from 'embercom/models/data/inbox/channels';

export default class Session extends Service {
  @tracked private _workspace?: Workspace;
  @tracked private _teammate?: AdminWithPermissions;
  @tracked private _workspacesList?: Array<WorkspaceForList>;
  id: string;

  @service declare intl: IntlService;
  @service modelDataCacheService: any;
  @service declare store: Store;
  @service intercomEventService: any;
  @service declare regionService: RegionService;
  @service declare copilotApi: CopilotApi;
  @service declare suiteMigrationService: SuiteMigrationService;

  @service declare finStandaloneService: FinStandaloneService;

  // for <Inbox2::Adapters::Inbox1 />
  @tracked inbox1Status: 'unknown' | 'booting' | 'booted' = 'unknown';

  constructor() {
    super(...arguments);

    this.id = generateUUID();
  }

  get isWorkspaceLoaded(): boolean {
    return !!this._workspace;
  }

  get isTeammateLoaded(): boolean {
    return !!this._teammate;
  }

  get isWorkspacesListLoaded(): boolean {
    return !!this._workspacesList;
  }

  get workspace(): Workspace {
    if (!this._workspace) {
      throw new Error(
        'Attempted to access the workspace before it is set. Please call session.switchWorkspace first.',
      );
    }

    return this._workspace;
  }

  get teammate(): AdminWithPermissions {
    if (!this._teammate) {
      throw new Error(
        `Attempted to access the teammate before it is set. Please call session.getTeammate first.`,
      );
    }
    return this._teammate;
  }

  get workspacesList(): Array<WorkspaceForList> {
    if (!this._workspacesList) {
      throw new Error(
        `Attempted to access the workspacesList before it is set. Please call session.getWorkspaces first.`,
      );
    }
    return this._workspacesList;
  }

  get isCopilotEnabled() {
    return this.copilotApi.isCopilotEnabled;
  }

  switchWorkspace(id: string): void {
    if (this._workspace?.id === id) {
      return;
    }
    this._workspace = new Workspace(id);
  }

  async getWorkspaces() {
    this._workspacesList = (await get(`/ember/admins/apps.json`)) as Array<WorkspaceForList>;
  }

  async getTeammate(workspaceId: string) {
    let data: AdminWithPermissionsWireFormat = await get(
      `/ember/admins/mini_me.json?app_id=${workspaceId}`,
    );

    this._teammate = AdminWithPermissions.deserialize(data);
  }

  async updateTeammate(params: Record<string, unknown>) {
    let data = {
      app_id: this.workspace.id,
      id: this.teammate.id,
      ...params,
    };
    return await put(`/ember/admins/${this.teammate.id}`, data);
  }

  directlySetTeammate(teammate: AdminWithPermissions | AdminSummary) {
    if (ENV.environment !== 'test') {
      throw new Error(`You cannot directly set the teammate outside of tests`);
    }

    // allow using an AdminSummary in tests
    if (teammate instanceof AdminWithPermissions) {
      this._teammate = teammate;
    } else if (teammate instanceof AdminSummary) {
      this._teammate = new AdminWithPermissions(
        teammate.id,
        teammate.name,
        teammate.imageURL,
        new AdminPermissions({
          conversationAccess: {},
          roleId: null,
          hasInboxAccess: true,
          canManageTeammates: true,
          canChangeAwayMode: true,
          canManageTags: true,
          canManageMacros: true,
          canCreateMacros: true,
          canEditMacros: true,
          canDeleteMacros: true,
          canInboxAccessCopilot: true,
          hasFullAccess: true,
          canInboxConversationTranscriptsExport: true,
          canManageInboxViews: true,
          canManageInboxViewsPinnedColumns: true,
          canManageSharedCustomFolders: true,
          canAccessRealTimeDashboard: true,
          canReassignConversations: true,
          canRedactConversationParts: true,
          canSendFromCustomAddresses: true,
          canReplyToInboundConversations: true,
          canAccessWorkspaceSettings: true,
          canManageEmojis: true,
          canManageConversationParticipants: true,
          canManageConversationCreation: true,
          canMergeConversations: true,
          canAccessContacts: true,
          canAccessBillingSettings: true,
          canAccessAllConversations: true,
          canListenAndBargeOnCalls: true,
          canDeleteCallRecordings: true,
          canManageKnowledgeBaseContent: true,
          canRemoveConversationSlas: true,
          canAccessReporting: true,
          canUsePersonalMacros: true,
          canUseDataConnectors: true,
          canReassignConversationsOnAway: true,
          canEditOwnProfile: true,
          canEditOtherProfiles: true,
          canEditNotes: true,
        }),
        [],
        [],
        DEFAULT_LOCALE,
        false,
        false,
      );
    }
  }

  get locale() {
    return this.intl.primaryLocale;
  }

  @action async changeLanguage(
    newLocale: ValidLocales,
    app = this.workspace as any,
    admin = this.teammate as any,
  ) {
    let data = {
      app_id: app.id,
      id: admin.id,
      locale: newLocale,
    };
    await put(`/ember/admins/update_locale`, data);
    await this.intl.switchLocale(newLocale);
    if (this.isInbox2Booted) {
      admin.locale = newLocale;
      this.modelDataCacheService.clear();
    } else {
      this.store.pushPayload({ admin: { id: admin.id, locale: newLocale } });
      admin.updateLocalCache(this.modelDataCacheService);
      app.updateLocalCache(this.modelDataCacheService);
    }
    this.trackChangeLocale(newLocale, this.isInbox2Booted ? 2 : 1);
  }

  get showLightInbox() {
    return (
      this.teammate.hasVbp2CollaboratorSeat ||
      this.teammate.hasPricing5LiteSeat ||
      this.workspace.hasLightStandaloneInbox
    );
  }

  shouldShowExternalConversationMode(conversation: Conversation): boolean {
    return (
      (!!conversation.externalType &&
        this.workspace.hasStandaloneToSuiteMigrationFeature &&
        this.suiteMigrationService.forceSuiteVersion) ||
      conversation.currentChannel === Channel.FinAgentApi
    );
  }

  get skipCanReplyToInboundCheck() {
    return (
      this.teammate.permissions.canReplyToInboundConversations ||
      !this.workspace.isFeatureEnabled('multiple_seat_types')
    );
  }

  private get isInbox2Booted() {
    return !!this._workspace;
  }

  private trackChangeLocale(newLocale: ValidLocales, inboxVersion: 1 | 2) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'switched',
      object: 'language',
      section: 'admin_details',
      language: newLocale,
      inbox_version: inboxVersion,
    });
  }

  get mainStandalonePlatform() {
    return this.finStandaloneService.platform;
  }
}

declare module '@ember/service' {
  interface Registry {
    session: Session;
  }
}
